import React, { useMemo } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { Group, Tooltip } from '@mantine/core'
import { formatMicroDenom } from '@/wallet-utils'
import {
  stakeModeAtom,
  withdrawStTokenToDexModalIsOpenAtom,
  withdrawStTokenToDexTransactionAtom
} from '@/page-components/Stake/atoms'
import { StepperProvider, StepperModal, useStepper, Icon, InlineLoader } from '@/components'
import { useLiquidStakeTransactionAmountQuery } from './queries'
import { useWithdrawStTokenToDex } from './WithdrawStTokenToDexProvider'
import { WithdrawStTokenToDexStepOne } from './WithdrawStTokenToDexStepOne'
import { WithdrawStTokenToDexStepTwo } from './WithdrawStTokenToDexStepTwo'
import { CHAIN_INFO_LIST, getDexInfoFromDenom } from '@/config'
import { useSelectedDenom } from '../../../contexts/denom'
import { useDexChain } from '@/contexts/wallet'

const WithdrawStTokenToDexModal: React.FC = () => {
  const {
    resetWithdrawStTokenToDexState,
    signStakeToWithdrawStTokenToDexError,
    isBroadcastingStakeToWithdrawStTokenToDex,
    broadcastStakeToWithdrawStTokenToDexError,
    ibcStatusError,
    isIbcStatusLoading
  } = useWithdrawStTokenToDex()

  const { data: stakeTransactionAmount, error: stakeTransactionAmountError } = useLiquidStakeTransactionAmountQuery()

  const [isOpen, setIsOpen] = useAtom(withdrawStTokenToDexModalIsOpenAtom)

  const setTransaction = useSetAtom(withdrawStTokenToDexTransactionAtom)

  const setStakeMode = useSetAtom(stakeModeAtom)

  const [denom] = useSelectedDenom()

  // Just making sure dex is prefetched and that it does not get garbage collected
  useDexChain()

  const steps = useMemo(() => {
    const dexInfo = getDexInfoFromDenom(denom)

    if (dexInfo == null) {
      return []
    }

    const chainInfo = CHAIN_INFO_LIST[dexInfo.coinDenom]

    return [`Transfer st${denom} to ${chainInfo.chainName}`, `LP on ${dexInfo.name}`]
  }, [denom])

  const handleClose = () => {
    setIsOpen(false)
    setTransaction(null)
    setStakeMode(null)
    resetWithdrawStTokenToDexState()
  }

  const stepper = useStepper({
    steps,
    warning: isIbcStatusLoading,
    error: Boolean(signStakeToWithdrawStTokenToDexError || broadcastStakeToWithdrawStTokenToDexError || ibcStatusError),
    isOpen,
    isLoading: isBroadcastingStakeToWithdrawStTokenToDex || isIbcStatusLoading,
    isPostError: false,
    isComplete: false,
    onClose: handleClose
  })

  const renderAmount = () => {
    if (stakeTransactionAmountError) {
      return (
        <Group spacing="xs">
          <Tooltip label="Unable to load staked amount at this time." withArrow position="right">
            <Icon name="warning" sx={(t) => ({ color: t.colors.yellow[6] })} />
          </Tooltip>
          st{denom}
        </Group>
      )
    }

    if (stakeTransactionAmount == null) {
      return (
        <Group spacing="xs">
          <InlineLoader />
          <span>st{denom}</span>
        </Group>
      )
    }

    const chainInfo = CHAIN_INFO_LIST[denom]

    return (
      <span>
        {formatMicroDenom(stakeTransactionAmount.amount, chainInfo.stakeCurrency.coinMinimalDenom, 5)} st{denom}
      </span>
    )
  }

  return (
    <StepperProvider {...stepper}>
      <StepperModal title="Add to DEX" amount={renderAmount()}>
        <WithdrawStTokenToDexStepOne />
        <WithdrawStTokenToDexStepTwo />
      </StepperModal>
    </StepperProvider>
  )
}

export { WithdrawStTokenToDexModal }
